import {
  type LimitOffsetPaginatorMessage,
  type Message,
} from '@sunrise-ui/api/messenger';
import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';
import { conversationsApi } from './api';
import { assertParameterIsNotEmptyString } from './error';

// Message status enum for better extensibility
export enum MessageStatus {
  CONFIRMED = 'confirmed',
  PENDING = 'pending',
  FAILED = 'failed',
}

// Augmented Message type to avoid inline type casting
export type MessageWithStatus = Message & {
  status?: MessageStatus;
};

// Type for infinite query data structure
type MessagesInfiniteData = InfiniteData<{
  results: MessageWithStatus[];
  count: number;
  limit?: number;
  offset?: number;
  next?: string;
  previous?: string;
}>;

export const getMessages = async (
  conversationId: string,
  pageParam: number,
  pageSize: number,
) => {
  assertParameterIsNotEmptyString(conversationId, 'conversationId');

  const response =
    await conversationsApi.getMessagesConversationsConversationIdMessagesGet({
      conversationId,
      offset: pageParam,
      limit: pageSize,
    });
  return response.data;
};

// Add optimistic message to the first page
export const addOptimisticMessage = (
  optimisticMessage: MessageWithStatus,
  queryKey: QueryKey,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData<MessagesInfiniteData>(queryKey, (old) => {
    if (!old) return old;

    return {
      ...old,
      pages: old.pages.map((page, index: number) => {
        if (index !== 0) return page;

        let hasOptimisticMessage = false;
        for (const message of page.results) {
          if (message._id === optimisticMessage._id) {
            hasOptimisticMessage = true;
            break;
          }
        }

        if (hasOptimisticMessage) {
          return page;
        }

        return {
          ...page,
          results: [...page.results, optimisticMessage],
          count: page.count + 1,
        };
      }),
    };
  });
};

// Remove failed message from cache
export const removeFailedMessage = (
  tempId: string,
  queryKey: QueryKey,
  queryClient: QueryClient,
): boolean => {
  let messageWasRemoved = false;

  queryClient.setQueryData<MessagesInfiniteData>(queryKey, (old) => {
    if (!old) return old;

    return {
      ...old,
      pages: old.pages.map((page) => {
        const filteredResults = page.results.filter((msg) => {
          if (msg._id === tempId) {
            messageWasRemoved = true;
            return false; // remove it
          }
          return true; // keep it
        });

        return {
          ...page,
          results: filteredResults,
        };
      }),
    };
  });

  return messageWasRemoved;
};

// Mark message as failed
export const markMessageFailed = (
  tempId: string,
  queryKey: QueryKey,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData<MessagesInfiniteData>(queryKey, (old) => {
    if (!old) return old;

    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        results: page.results.map((msg) =>
          msg._id === tempId ? { ...msg, status: MessageStatus.FAILED } : msg,
        ),
      })),
    };
  });
};

// Confirm optimistic message across all pages (but only the first match)
export const confirmOptimisticMessage = (
  content: string,
  confirmedMessage: Message,
  queryKey: QueryKey,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData<MessagesInfiniteData>(queryKey, (old) => {
    if (!old) return old;

    let messageConfirmed = false;

    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        results: page.results.map((msg) => {
          if (
            !messageConfirmed &&
            msg._id?.toString().startsWith('temp-') &&
            msg.content === content
          ) {
            messageConfirmed = true;
            return { ...confirmedMessage, status: MessageStatus.CONFIRMED };
          }
          return msg;
        }),
      })),
    };
  });
};

// Legacy function - replaced by cleaner updater functions above
export const updateMessagesCache = (
  message: Message,
  queryKey: QueryKey,
  queryClient: QueryClient,
) => {
  const messagesQueryState = queryClient.getQueryState(queryKey);
  if (messagesQueryState?.fetchStatus === 'fetching') return;

  const queryMessagesUpdater = (
    oldData: InfiniteData<LimitOffsetPaginatorMessage> | undefined,
  ) => {
    if (!oldData) return;

    const { pageParams, pages } = oldData;
    const updatedPageParams = [...pageParams];
    const updatedCount = pages[0].count + 1;
    const pageSize = pages[0].limit;
    const lastPageIndex = pages.length - 1;
    let carryOverMessage = message;
    let lastPageOffset = pageSize;

    const updatedPages = pages.map((page, index) => {
      const isLastPage = index === pages.length - 1;
      const isFullPage = page.results.length === pageSize;

      if (isFullPage) {
        lastPageOffset = page.offset;
        const [oldestMessage, ...latestMessages] = page.results;
        latestMessages.push(carryOverMessage);
        carryOverMessage = oldestMessage;

        return {
          ...page,
          count: updatedCount,
          next: isLastPage
            ? `/messages/?offset=${lastPageOffset + pageSize}&limit=${pageSize}`
            : page.next,
          results: latestMessages,
        };
      }

      return {
        ...page,
        count: updatedCount,
        results: [...page.results, carryOverMessage],
      };
    });

    if (pages[lastPageIndex].results.length === pageSize) {
      updatedPages.push({
        count: updatedCount,
        limit: pageSize,
        previous: `/messages/?offset=${lastPageOffset}&limit=${pageSize}`,
        offset: lastPageOffset + pageSize,
        results: [carryOverMessage],
      });
      updatedPageParams.push(lastPageOffset + pageSize);
    }

    return {
      ...oldData,
      pageParams: updatedPageParams,
      pages: updatedPages,
    };
  };

  queryClient.setQueryData<InfiniteData<LimitOffsetPaginatorMessage>>(
    queryKey,
    queryMessagesUpdater,
  );
};
